import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environment/environment';

/**
 * Interface correspondant au NotificationDTO du backend
 */
export interface NotificationDTO {
  id: string;
  type: string;
  title: string;
  message: string;
  priority: string;
  read: boolean;
  recipientId: string;
  recipientRole: string;
  relatedEntityId?: string;
  relatedEntityType?: string;
  metadata?: any;
  action?: string;
  createdAt: string;
  readAt?: string;
}

/**
 * Interface pour la réponse paginée du backend Spring
 */
export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
}

/**
 * Service pour interagir avec l'API REST des notifications
 * Correspond aux endpoints de NotificationController.java
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationApiService {
  private readonly apiUrl = `${environment.apiURL}/notifications`;

  constructor(private http: HttpClient) {}

  /**
   * GET /api/notifications?page=0&size=20
   * Récupère toutes les notifications avec pagination
   */
  getNotifications(
    page: number = 0,
    size: number = 20,
  ): Observable<PageResponse<NotificationDTO>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.http.get<PageResponse<NotificationDTO>>(this.apiUrl, {
      params,
    });
  }

  /**
   * GET /api/notifications/unread
   * Récupère uniquement les notifications non lues
   */
  getUnreadNotifications(): Observable<NotificationDTO[]> {
    return this.http.get<NotificationDTO[]>(`${this.apiUrl}/unread`);
  }

  /**
   * GET /api/notifications/unread/count
   * Compte le nombre de notifications non lues
   */
  getUnreadCount(): Observable<{ unreadCount: number }> {
    return this.http.get<{ unreadCount: number }>(
      `${this.apiUrl}/unread/count`,
    );
  }

  /**
   * PUT /api/notifications/{notificationId}/read
   * Marque une notification comme lue
   */
  markAsRead(notificationId: string): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(
      `${this.apiUrl}/${notificationId}/read`,
      {},
    );
  }

  /**
   * PUT /api/notifications/read-all
   * Marque toutes les notifications comme lues
   */
  markAllAsRead(): Observable<{ message: string; count: number }> {
    return this.http.put<{ message: string; count: number }>(
      `${this.apiUrl}/read-all`,
      {},
    );
  }

  /**
   * DELETE /api/notifications/{notificationId}
   * Supprime une notification
   */
  deleteNotification(notificationId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(
      `${this.apiUrl}/${notificationId}`,
    );
  }
}
